import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CreditScoreCompanyDto,
  CreditScoreContributionDto,
} from '@interfaces/credit-score/responses/credit-score.response';
import { DateFormatterService } from '@src/app/core/services/helpers/date-formatter.service';
import { CreditScoreHttpService } from '@services/http/credit-score/credit-score-http.service';
import { SharedModule } from '@shared/shared.module';
import { DrawerModule } from 'primeng/drawer';
import { catchError, map, of, Subject, switchMap, tap } from 'rxjs';
import {
  clientMetricLabel,
  formatClientMetricValue,
  formatDecimal,
  joinWithAnd,
} from '@features/dashboard/performance/pages/credit-score/helpers/credit-score-format';
import { scoreHistory } from '@features/dashboard/performance/pages/credit-score/helpers/credit-score-history';
import {
  creditRating,
  ratingDistance,
} from '@features/dashboard/performance/pages/credit-score/helpers/credit-score-rating';

type LoadResult = { ok: true; data: CreditScoreCompanyDto | null } | { ok: false };

interface CompositionRow {
  key: string;
  label: string;
  value: string;
  score: string;
  estimated: boolean;
  missing: boolean;
}

interface HistoryBar {
  year: number;
  score: number;
  heightPct: number;
  current: boolean;
}

/**
 * Bloco do score de crédito no topo da Análise Econômica (card 3766, opção A do protótipo): faixa
 * compacta com nota, rating equivalente, cobertura, variação e "Ver composição", que abre uma gaveta
 * com os indicadores e o histórico. Dados: `GET credit-score/companies/{companyId}`, refeito quando a
 * empresa muda. Quem decide se aparece é o `dfp.component.html` (empresa escolhida, demonstrativos
 * validados, fora do modo parceiro/embutido).
 */
@Component({
  selector: 'app-credit-score-strip',
  standalone: true,
  imports: [SharedModule, DrawerModule],
  templateUrl: './credit-score-strip.component.html',
  styleUrls: ['./credit-score-strip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditScoreStripComponent {
  private readonly http = inject(CreditScoreHttpService);
  private readonly currentYear = inject(DateFormatterService).getTodayDate().getFullYear();

  readonly companyId = input.required<string>();

  readonly loading = signal(true);
  readonly failed = signal(false);
  readonly company = signal<CreditScoreCompanyDto | null>(null);
  readonly drawerOpen = signal(false);

  private readonly requests$ = new Subject<string>();

  readonly hasScore = computed(() => this.company()?.score != null);
  readonly score = computed(() => this.company()?.score ?? null);
  readonly year = computed(() => this.company()?.referenceYear ?? null);
  /** Exercício em curso: a nota usa o último balancete e pode mudar no fechamento. */
  readonly partial = computed(() => this.year() === this.currentYear);
  /** Rating que o backend calculou para o score; a escala local só cobre resposta sem rating. */
  readonly rating = computed(() => this.company()?.rating ?? creditRating(this.score()));
  readonly distance = computed(() => ratingDistance(this.score()));
  readonly ringPct = computed(() => (this.score() ?? 0) / 10);
  readonly coverageLabel = computed(() => `${formatDecimal(this.company()?.coverage ?? 0, 0)}%`);

  readonly history = computed(() => scoreHistory(this.company()?.scoreHistory));

  /** Variação contra o exercício anterior com score. */
  readonly variation = computed(() => {
    const year = this.year();
    const history = this.history();
    const current = history.find(item => item.year === year);
    const previous = [...history].reverse().find(item => year != null && item.year < year);
    if (!current || !previous) return null;
    const delta = current.score - previous.score;
    return {
      year: previous.year,
      delta,
      text: `${delta > 0 ? '▲' : delta < 0 ? '▼' : '='} ${Math.abs(delta)} pts`,
      tone: delta > 0 ? 'up' : delta < 0 ? 'down' : 'neutral',
    };
  });

  readonly nextRatingLabel = computed(() => {
    const distance = this.distance();
    if (distance.nextRating == null) return 'Teto da escala (AAA)';
    return `${distance.pointsToNext} pts para ${distance.nextRating}`;
  });

  private readonly orderedContributions = computed<CreditScoreContributionDto[]>(() => {
    const list = this.company()?.contributions ?? [];
    return [
      ...list.filter(item => item.block === 'financial'),
      ...list.filter(item => item.block === 'sector'),
    ];
  });

  readonly generalRows = computed(() =>
    this.toRows(this.orderedContributions().filter(item => item.block === 'financial'))
  );
  readonly sectorRows = computed(() =>
    this.toRows(this.orderedContributions().filter(item => item.block === 'sector'))
  );

  /** "Só 2 de 6 indicadores puderam ser calculados (cobertura 35%). Faltam A, B e C do último exercício." */
  readonly insufficientMessage = computed(() => {
    const contributions = this.orderedContributions();
    const available = contributions.filter(item => item.value != null).length;
    const missing = contributions.filter(item => item.value == null).map(clientMetricLabel);
    const head = `Só ${available} de ${contributions.length} indicadores puderam ser calculados (cobertura ${this.coverageLabel()}).`;
    if (!missing.length) return head;
    return `${head} ${missing.length === 1 ? 'Falta' : 'Faltam'} ${joinWithAnd(missing)} do último exercício.`;
  });

  readonly historyBars = computed<HistoryBar[]>(() => {
    const year = this.year();
    return this.history().map(item => ({
      year: item.year,
      score: item.score,
      heightPct: Math.max(4, item.score / 10),
      current: item.year === year,
    }));
  });

  constructor() {
    this.requests$
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.failed.set(false);
          this.drawerOpen.set(false);
        }),
        switchMap(companyId =>
          this.http.getCompany(companyId).pipe(
            map((data): LoadResult => ({ ok: true, data })),
            catchError(() => of<LoadResult>({ ok: false }))
          )
        ),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe(result => {
        this.loading.set(false);
        if (!result.ok) {
          this.failed.set(true);
          this.company.set(null);
          return;
        }
        this.company.set(result.data);
        this.failed.set(!result.data);
      });

    // Refaz a leitura quando a empresa da tela muda.
    effect(() => {
      const companyId = this.companyId();
      untracked(() => this.requests$.next(companyId));
    });
  }

  retry(): void {
    this.requests$.next(this.companyId());
  }

  private toRows(list: CreditScoreContributionDto[]): CompositionRow[] {
    return list.map(item => ({
      key: item.key,
      label: clientMetricLabel(item),
      value: formatClientMetricValue(item.value, item.unit),
      score: item.score == null ? 'sem dado' : formatDecimal(item.score, 0),
      estimated: item.source === 'estimated',
      missing: item.value == null,
    }));
  }
}
